import {
  LayoutDashboard,
  ListChecks,
  ShieldCheck,
  BarChart3,
  Users,
  ImageUp,
  CreditCard,
  PlusCircle,
  ShieldPlus,
  Wallet,
  ShoppingCart,
  History,
  Building2,
  Tags,
  Settings2,
  Package,
  Gift,
  DatabaseBackup,
  ClipboardList,
  Megaphone,
  FileText,
  MessageCircle,
  Settings,
} from "lucide-react";

const hasAny = (...allowed) => (roles) => allowed.some((r) => roles.includes(r));

/**
 * Sidebar navigation, grouped into sections.
 * `visible(roles)` mirrors the visibility rules the sidebar had before the redesign
 * (these only control the menu; the routes keep their own ProtectedRoute role checks).
 */
export const NAV_SECTIONS = [
  {
    title: "Overview",
    items: [
      { text: "Dashboard", to: "/dashboard", icon: LayoutDashboard, visible: (roles) => !roles.includes("customer_user") },
      { text: "Dashboard", to: "/customer_dashboard", icon: LayoutDashboard, visible: hasAny("customer_user") },
    ],
  },
  {
    title: "Gateways",
    items: [
      { text: "Gateway Whitelist", to: "/whitelist", icon: ListChecks, visible: hasAny("super_admin") },
      { text: "Warranty Management", to: "/warranty", icon: ShieldCheck, visible: hasAny("super_admin"), end: true },
      { text: "Gateway Analytics", to: "/analytics", icon: BarChart3, visible: hasAny("super_admin") },
      { text: "User Management", to: "/device", icon: Users, visible: hasAny("super_admin") },
      { text: "Gateway Image", to: "/image_upload", icon: ImageUp, visible: hasAny("super_admin") },
    ],
  },
  {
    title: "My Subscriptions",
    items: [
      { text: "Active Subscriptions", to: "/subscription/active", icon: CreditCard, visible: hasAny("customer_user") },
      { text: "Subscribe Gateways", to: "/customer_subscription", icon: PlusCircle, visible: hasAny("customer_user") },
      { text: "Extended Warranty", to: "/warranty/extended", icon: ShieldPlus, visible: hasAny("customer_user") },
    ],
  },
  {
    title: "Billing",
    items: [
      { text: "Pending Payments", to: "/payments/pending", icon: Wallet, visible: hasAny("customer_user") },
      { text: "Pending Orders", to: "/order/pending", icon: ShoppingCart, visible: hasAny("customer_user") },
      { text: "Payment History", to: "/payments/history", icon: History, visible: hasAny("customer_user") },
    ],
  },
  {
    title: "CMS",
    items: [
      { text: "CMS Management", to: "/cms", icon: Building2, visible: hasAny("super_admin", "cms_admin", "account_admin") },
    ],
  },
  {
    title: "Subscription Admin",
    items: [
      { text: "Subscription Plans", to: "/subscription/plans", icon: Tags, visible: hasAny("super_admin", "clerical_admin") },
      { text: "Manage Subscriptions", to: "/admin/subscriptions", icon: Settings2, visible: hasAny("super_admin"), end: true },
      { text: "Add-On Catalog", to: "/admin/addons/catalog", icon: Package, visible: hasAny("super_admin") },
      { text: "Grant Add-Ons", to: "/admin/addons/grant", icon: Gift, visible: hasAny("super_admin") },
      { text: "Extended Warranty", to: "/admin/warranty/extended", icon: ShieldPlus, visible: hasAny("super_admin") },
      { text: "Backfill Trial Subs", to: "/admin/subscriptions/backfill-trial", icon: DatabaseBackup, visible: hasAny("super_admin") },
    ],
  },
  {
    title: "Content",
    items: [
      { text: "Product Info", to: "/admin/product-info", icon: ClipboardList, visible: hasAny("super_admin") },
      { text: "Push Broadcast", to: "/admin/notifications/broadcast", icon: Megaphone, visible: hasAny("super_admin") },
    ],
  },
];

export const FOOTER_LINKS = [
  { text: "Terms & Conditions", href: "https://www.senzo.com.my/crossguard_tnc/", icon: FileText },
  { text: "Contact Us", href: "https://www.senzo.my/contact-us", icon: MessageCircle },
  // Previously shown to everyone, but the /settings route only admits these roles.
  { text: "System Settings", to: "/settings", icon: Settings, visible: hasAny("super_admin", "clerical_admin") },
];

/** Sections with only the items this user can see; empty sections dropped. */
export function getVisibleSections(roles = []) {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.visible(roles)),
  })).filter((section) => section.items.length > 0);
}

/** Title for the current path (longest matching nav entry), for the topbar. */
export function getPageTitle(pathname) {
  const all = [...NAV_SECTIONS.flatMap((s) => s.items), ...FOOTER_LINKS.filter((l) => l.to)];
  const match = all
    .filter((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.text || "";
}
